
import Vue, { PropType } from 'vue';
import { apiFetch } from '@/services/apiClient';

export default Vue.extend({
  name: 'CreateGameModal',

  props: {
    brandOptions: {
      type: Array as PropType<Array<{ id: string; name: string; filePath: string; brandId: string }>>,
      default: () => [],
    },
  },

  data() {
    return {
      name: '',
      gameType: '',
      selectedCategory: null as string | null,
      thumbFile: null as File | null,
      thumbPreview: '' as string,
      placeholderThumb: require('@/assets/thumbnails/coming_soon_default.png') as string,
      saving: false,
    };
  },

  computed: {
    isValid(): boolean {
      return !!this.name.trim() && !!this.selectedCategory;
    },
  },

  beforeDestroy() {
    if (this.thumbPreview && this.thumbPreview.startsWith('blob:')) {
      URL.revokeObjectURL(this.thumbPreview);
    }
  },

  methods: {
    triggerThumbUpload() {
      (this.$refs.thumbInput as HTMLInputElement).click();
    },

    onThumbSelected(event: Event) {
      const input = event.target as HTMLInputElement;
      const file = input.files?.[0];
      if (!file) return;
      input.value = '';
      if (this.thumbPreview && this.thumbPreview.startsWith('blob:')) {
        URL.revokeObjectURL(this.thumbPreview);
      }
      this.thumbFile = file;
      this.thumbPreview = URL.createObjectURL(file);
    },

    removeThumb() {
      if (this.thumbPreview && this.thumbPreview.startsWith('blob:')) {
        URL.revokeObjectURL(this.thumbPreview);
      }
      this.thumbFile = null;
      this.thumbPreview = '';
    },

    async save() {
      if (!this.isValid) return;

      const cat = (this.brandOptions as Array<{ id: string; name: string; filePath: string; brandId: string }>)
        .find((b) => b.id === this.selectedCategory);
      if (!cat) return;

      this.saving = true;
      try {
        // Step 1: Create game
        const createRes = await apiFetch('/api/games', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            brand_id: cat.brandId,
            file_path: cat.filePath,
            name: this.name.trim(),
            data: {
              name: this.name.trim(),
              img: 'thumbnails/coming_soon_default.png',
              gameFiles: [],
              ...(this.gameType.trim() ? { gameType: this.gameType.trim() } : {}),
            },
          }),
        });
        if (!createRes.ok) throw new Error(`Create failed (${createRes.status})`);
        const newGame = await createRes.json();

        // Step 2: Upload thumbnail if selected
        let thumbnailUrl = '';
        if (this.thumbFile) {
          const fd = new FormData();
          fd.append('file', this.thumbFile);
          const thumbRes = await apiFetch(`/api/games/${newGame.id}/thumbnail`, {
            method: 'POST',
            body: fd,
          });
          if (thumbRes.ok) {
            const { url } = await thumbRes.json();
            thumbnailUrl = url;
          }
        }

        // Step 3: Update data with thumbnail if we got one
        if (thumbnailUrl) {
          const putRes = await apiFetch(`/api/games/${newGame.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              data: { ...newGame.data, img: thumbnailUrl },
            }),
          });
          if (putRes.ok) {
            newGame.data = { ...newGame.data, img: thumbnailUrl };
          }
        }

        this.$emit('created', newGame);
        // Keep saving=true so the button stays disabled while the parent closes the modal
      } catch (e) {
        console.error('Failed to create game', e);
        (this as any).$toast?.error('Failed to create game. Please try again.');
        this.saving = false;
      }
    },
  },
});
